import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Badge, Banner, ChipGroup, EmptyState, PageHeader, Spinner } from "@magarac/ui";
import { formatCents } from "@magarac/shared/money";
import { quoteSchema } from "@magarac/shared/schemas/documents";
import { z } from "zod";
import { HelpIcon } from "../../../components/HelpIcon";
import { api } from "../../../lib/api";
import { QUOTE_STATUS_LABELS, dayText, statusTone } from "../../../lib/documents";
import { messageFor } from "../../../lib/errors";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/quotes/")({
  component: QuotesPage,
});

type Filter = "all" | "draft" | "sent" | "viewed" | "approved" | "declined" | "expired";

/** CRM-17: every quote and where it stands. Screen key: quotes.list */
function QuotesPage() {
  const { active } = useTenant();
  const [filter, setFilter] = useState<Filter>("all");
  const q = useQuery({
    queryKey: ["quotes", active?.id, filter],
    queryFn: () =>
      api.get("/quotes", z.object({ quotes: z.array(quoteSchema) }), {
        query: filter === "all" ? {} : { status: filter },
      }),
    enabled: Boolean(active),
  });
  return (
    <div>
      <PageHeader title="Quotes" help={<HelpIcon screen="quotes.list" />} />
      <div className="mb-4">
        <ChipGroup
          label="Show"
          value={filter}
          onChange={setFilter}
          options={[
            { value: "all", label: "All" },
            { value: "draft", label: "Drafts" },
            { value: "sent", label: "Sent" },
            { value: "viewed", label: "Viewed" },
            { value: "approved", label: "Approved" },
            { value: "declined", label: "Declined" },
            { value: "expired", label: "Expired" },
          ]}
        />
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <Banner tone="danger">{messageFor(q.error)}</Banner>
      ) : q.data.quotes.length === 0 ? (
        <EmptyState
          title={filter === "all" ? "No quotes yet" : "No quotes here"}
          body="Start a quote from a job or a request: open it and choose New quote."
        />
      ) : (
        <ul className="divide-y divide-line rounded-card border border-line bg-surface">
          {q.data.quotes.map((x) => (
            <li key={x.id}>
              <Link
                to="/quotes/$id"
                params={{ id: x.id }}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 p-4 hover:bg-brand-50"
              >
                <span className="font-medium">#{x.number}</span>
                <span className="min-w-0 flex-1 truncate">{x.customerName}</span>
                <Badge tone={statusTone(x.status)}>{QUOTE_STATUS_LABELS[x.status]}</Badge>
                <span className="tabular-nums">{formatCents(x.totalCents)}</span>
                <span className="w-full text-sm text-muted sm:w-auto">
                  {x.sentAt ? `Sent ${dayText(x.sentAt)}` : `Drafted ${dayText(x.createdAt)}`}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
